import { normalizeCountryId } from './countrySettings';

const MEAL_CATEGORIES = new Set(['meals', 'food', 'food_and_drink']);
const FUEL_CATEGORIES = new Set(['vehicle_fuel']);
const TRAVEL_CATEGORIES = new Set(['travel_expenses', 'travel']);
const ENTERTAINMENT_CATEGORIES = new Set(['entertainment']);

const MEAL_KEYWORDS = /\b(food|drink|drinks|meal|meals|lunch|dinner|breakfast|takeaway|coffee|tea|cafe|restaurant|sandwich|snack)\b/i;
const PETROL_KEYWORDS = /\b(petrol|gasoline|unleaded)\b/i;
const DIESEL_KEYWORDS = /\b(diesel|green\s+diesel)\b/i;
const GENERIC_FUEL_KEYWORDS = /\b(fuel|forecourt|circle\s*k|applegreen|topaz|maxol)\b/i;
const NON_FUEL_TRAVEL_KEYWORDS = /\b(uber|lyft|taxi|cab|train|tram|bus|coach|flight|airfare|hotel|accommodation|parking|toll|tolls|mileage|car rental|rental car|ferry)\b/i;
const ENTERTAINMENT_KEYWORDS = /\b(entertainment|pub|bar|cinema|concert|show|ticket|night out)\b/i;

const normalizeRateValue = (rawRate) => {
  const parsedRate = Number(rawRate);
  if (!Number.isFinite(parsedRate) || parsedRate <= 0) return null;
  return parsedRate > 1 ? parsedRate / 100 : parsedRate;
};

const calculateVatFromGrossAmount = (grossAmount, rate) => {
  const normalizedGrossAmount = Number(grossAmount);
  const normalizedRate = normalizeRateValue(rate);
  if (!Number.isFinite(normalizedGrossAmount) || normalizedGrossAmount <= 0 || !Number.isFinite(normalizedRate) || normalizedRate <= 0) {
    return 0;
  }

  return normalizedGrossAmount - (normalizedGrossAmount / (1 + normalizedRate));
};

const toPositiveVatComponentTotal = (transaction = {}) => {
  if (!Array.isArray(transaction?.vatComponents)) {
    return 0;
  }

  const vatComponentTotal = transaction.vatComponents.reduce((sum, component) => {
    const amount = Number(component?.amount);
    return Number.isFinite(amount) && amount > 0 ? sum + amount : sum;
  }, 0);

  return vatComponentTotal > 0 ? parseFloat(vatComponentTotal.toFixed(2)) : 0;
};

const toPositiveVatAmount = (transaction = {}) => {
  const directVatAmount = Number(transaction?.vatAmount);
  if (Number.isFinite(directVatAmount) && directVatAmount > 0) {
    return directVatAmount;
  }

  const vatComponentTotal = toPositiveVatComponentTotal(transaction);
  if (vatComponentTotal > 0) {
    return vatComponentTotal;
  }

  const grossAmount = Number(transaction?.grossAmount);
  const amount = Number(transaction?.amount);
  const vatRate = normalizeRateValue(transaction?.vatRate);
  const fallbackGrossAmount = Number.isFinite(grossAmount) && grossAmount > 0
    ? grossAmount
    : amount;
  const inferredVatAmount = calculateVatFromGrossAmount(fallbackGrossAmount, vatRate);
  if (inferredVatAmount > 0) {
    return parseFloat(inferredVatAmount.toFixed(2));
  }

  return 0;
};

export const materializeExpenseVatFromRate = (
  transaction = {},
  {
    resolveTaxComponentDescription
  } = {}
) => {
  const transactionType = String(transaction?.type || '').trim().toLowerCase();
  if (!['expense', 'expenses'].includes(transactionType)) {
    return transaction;
  }

  const normalizedRate = normalizeRateValue(transaction?.vatRate);
  const amount = Number(transaction?.amount);
  const roundedAmount = Number.isFinite(amount) ? parseFloat(amount.toFixed(2)) : null;
  const directVatAmount = Number(transaction?.vatAmount);
  const roundedDirectVatAmount = Number.isFinite(directVatAmount) && directVatAmount > 0
    ? parseFloat(directVatAmount.toFixed(2))
    : null;
  const componentVatTotal = toPositiveVatComponentTotal(transaction);

  if (componentVatTotal > 0) {
    return {
      ...transaction,
      vatRate: normalizedRate ?? transaction?.vatRate ?? null,
      vatAmount: componentVatTotal,
      netAmount: roundedAmount !== null
        ? parseFloat(Math.max(roundedAmount - componentVatTotal, 0).toFixed(2))
        : transaction?.netAmount ?? null
    };
  }

  if (roundedDirectVatAmount !== null) {
    return {
      ...transaction,
      vatRate: normalizedRate ?? transaction?.vatRate ?? null,
      vatAmount: roundedDirectVatAmount,
      vatComponents: Array.isArray(transaction?.vatComponents) && transaction.vatComponents.length > 0
        ? transaction.vatComponents
        : (normalizedRate
            ? [{
                rate: normalizedRate,
                amount: roundedDirectVatAmount,
                description: typeof resolveTaxComponentDescription === 'function'
                  ? resolveTaxComponentDescription(normalizedRate)
                  : `${(normalizedRate * 100).toFixed(1)}% VAT`
              }]
            : []),
      netAmount: roundedAmount !== null
        ? parseFloat(Math.max(roundedAmount - roundedDirectVatAmount, 0).toFixed(2))
        : transaction?.netAmount ?? null
    };
  }

  const inferredVatAmount = calculateVatFromGrossAmount(amount, normalizedRate);
  if (!(inferredVatAmount > 0 && normalizedRate)) {
    return transaction;
  }

  const roundedVatAmount = parseFloat(inferredVatAmount.toFixed(2));
  return {
    ...transaction,
    vatRate: normalizedRate,
    vatAmount: roundedVatAmount,
    vatComponents: [{
      rate: normalizedRate,
      amount: roundedVatAmount,
      description: typeof resolveTaxComponentDescription === 'function'
        ? resolveTaxComponentDescription(normalizedRate)
        : `${(normalizedRate * 100).toFixed(1)}% VAT`
    }],
    netAmount: roundedAmount !== null
      ? parseFloat(Math.max(roundedAmount - roundedVatAmount, 0).toFixed(2))
      : transaction?.netAmount ?? null
  };
};

const collectCategoryIds = (transaction = {}) => {
  const categoryIds = new Set();
  const maybeAdd = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized) categoryIds.add(normalized);
  };

  maybeAdd(transaction.category);
  (transaction.items || []).forEach((item) => maybeAdd(item?.category));
  (transaction.splitItems || []).forEach((item) => maybeAdd(item?.category));
  (transaction.categoryBreakdown || []).forEach((item) => maybeAdd(item?.category));

  return categoryIds;
};

const collectSearchText = (transaction = {}) => {
  const parts = [
    transaction.description,
    transaction.notes,
    ...(Array.isArray(transaction.items) ? transaction.items.map((item) => item?.description) : []),
    ...(Array.isArray(transaction.splitItems) ? transaction.splitItems.map((item) => item?.description) : [])
  ];

  return parts
    .filter(Boolean)
    .join(' ')
    .trim();
};

export const getIrishBlockedExpenseVatWarning = ({
  transaction = {},
  vatSettings = {},
  currentCountry = {}
} = {}) => {
  const transactionType = String(transaction?.type || '').trim().toLowerCase();
  if (!['expense', 'expenses'].includes(transactionType)) return null;
  if (!(vatSettings?.enabled && vatSettings?.isRegistered)) return null;

  const normalizedCountryId = normalizeCountryId(currentCountry?.id || currentCountry?.name);
  if (normalizedCountryId !== 'ie') return null;

  const vatAmount = toPositiveVatAmount(transaction);
  if (vatAmount <= 0) return null;

  const categoryIds = collectCategoryIds(transaction);
  const searchText = collectSearchText(transaction);

  const hasMealMatch = [...categoryIds].some((categoryId) => MEAL_CATEGORIES.has(categoryId)) ||
    MEAL_KEYWORDS.test(searchText);
  const hasEntertainmentMatch = [...categoryIds].some((categoryId) => ENTERTAINMENT_CATEGORIES.has(categoryId)) ||
    ENTERTAINMENT_KEYWORDS.test(searchText);
  const hasFuelCategoryMatch = [...categoryIds].some((categoryId) => FUEL_CATEGORIES.has(categoryId));
  const hasTravelCategoryMatch = [...categoryIds].some((categoryId) => TRAVEL_CATEGORIES.has(categoryId));
  const hasPetrolKeywordMatch = PETROL_KEYWORDS.test(searchText);
  const hasDieselKeywordMatch = DIESEL_KEYWORDS.test(searchText);
  const hasGenericFuelTextMatch = GENERIC_FUEL_KEYWORDS.test(searchText);
  const hasNonFuelTravelKeywordMatch = NON_FUEL_TRAVEL_KEYWORDS.test(searchText);
  const hasAmbiguousTravelFuelMatch =
    hasTravelCategoryMatch &&
    !hasNonFuelTravelKeywordMatch &&
    !hasPetrolKeywordMatch &&
    !hasDieselKeywordMatch;
  const hasFuelMatch =
    hasFuelCategoryMatch ||
    hasPetrolKeywordMatch ||
    hasDieselKeywordMatch ||
    hasGenericFuelTextMatch ||
    hasAmbiguousTravelFuelMatch;

  let fuelType = null;
  if (hasPetrolKeywordMatch && !hasDieselKeywordMatch) {
    fuelType = 'petrol';
  } else if (hasDieselKeywordMatch && !hasPetrolKeywordMatch) {
    fuelType = 'diesel';
  } else if (hasFuelMatch) {
    fuelType = 'unknown';
  }

  const hasPetrolMatch = fuelType === 'petrol';
  const requiresFuelTypeSelection = fuelType === 'unknown';

  if (!hasMealMatch && !hasPetrolMatch && !hasEntertainmentMatch && !requiresFuelTypeSelection) {
    return null;
  }

  if (requiresFuelTypeSelection && !hasMealMatch && !hasEntertainmentMatch) {
    const allowNonFuelChoice = hasAmbiguousTravelFuelMatch && !hasFuelCategoryMatch;
    return {
      hasMealMatch,
      hasPetrolMatch: false,
      hasEntertainmentMatch,
      hasFuelMatch,
      hasTravelCategoryMatch,
      fuelType,
      requiresFuelTypeSelection: true,
      allowNonFuelChoice,
      vatAmount: parseFloat(vatAmount.toFixed(2)),
      action: 'ask_fuel_type',
      message: allowNonFuelChoice
        ? 'This travel expense includes VAT. If this is petrol, VAT should not be reclaimed in Ireland. Is this petrol, diesel, or another travel expense? If it is petrol, Use No VAT for future entries.'
        : 'This vehicle fuel expense includes VAT. In Ireland VAT can usually be reclaimed on diesel, but not on petrol. Is this diesel or petrol? If it is petrol, Use No VAT for future entries.'
    };
  }

  let reasonLabel = 'food, meals, takeaways, entertainment, or petrol';
  if (hasMealMatch && !hasPetrolMatch && !hasEntertainmentMatch) reasonLabel = 'food, drink, meals, or takeaways';
  if (!hasMealMatch && hasPetrolMatch && !hasEntertainmentMatch) reasonLabel = 'petrol';
  if (!hasMealMatch && !hasPetrolMatch && hasEntertainmentMatch) reasonLabel = 'entertainment';

  return {
    hasMealMatch,
    hasPetrolMatch,
    hasEntertainmentMatch,
    hasFuelMatch,
    fuelType,
    requiresFuelTypeSelection: false,
    vatAmount: parseFloat(vatAmount.toFixed(2)),
    action: 'clear_vat',
    message: `This expense includes a VAT amount, but VAT on ${reasonLabel} should not be reclaimed in Ireland. Press OK to remove the VAT and save, or Cancel to keep editing. Use No VAT for future entries like this.`
  };
};

export const clearVatFromTransaction = (transaction = {}) => ({
  ...transaction,
  netAmount: Number.isFinite(Number(transaction?.amount))
    ? parseFloat(Number(transaction.amount).toFixed(2))
    : transaction?.netAmount ?? null,
  vatAmount: 0,
  vatRate: 0,
  vatComponents: [],
  items: Array.isArray(transaction.items)
    ? transaction.items.map((item) => ({
        ...item,
        vatAmount: 0,
        vatRate: 0,
        vatReclaimable: false
      }))
    : transaction.items
});

export default getIrishBlockedExpenseVatWarning;
